<template>
  <div class="video-uploader" v-if="this.$store.state.isReady">
    <section class="sortable-list">
      <draggable
        class="list-group"
        v-model="videos"
        :options="dragOptions"
        handle=".handle"
      >
        <transition-group tag="ul" name="fade">
          <VideoRow
            v-for="v in videos"
            :key="v.uuid"
            v-bind="v"
          />
        </transition-group>
      </draggable>
    </section>

    <section
      v-if="shouldShowDropzoneAndUploadButton"
      class="drop-zone-and-upload-button"
    >
      <div @drop.prevent="handleDrop($event)"
           :class="{'drop-active': dropIncoming}"
           class="button-row drop-target" @dragover.prevent
           @dragenter.prevent="dropZoneEnter"
           @dragleave.prevent="dropZoneLeave"
      >
        {{this.dropzoneMessage}}
      </div>

      <div class="note button-row">– or –</div>

      <div class="button-row">
        <div class="upload-button">
          <input
            id="fileToUpload"
            ref="fileToUpload"
            type="file"
            :accept="acceptAttrib"
            @change="startUploading"
          />
          <label for="fileToUpload" class="cf-btn-secondary">Select file to upload</label>
        </div>

        <div v-if="this.util.isTargetedForStory()">
          <!--Somehow, when using button element, cf-btn-secondary style doesn't apply, so using label -->
          <label
            id="videoPickerButton"
            class="cf-btn-secondary"
            @click="openVideoPicker"
          >
            Insert existing videos
          </label>
        </div>
      </div>
    </section>

  </div>
</template>

<script>
import Vue from 'vue';
import draggable from 'vuedraggable';
import VideoRow from './VideoRow.vue';
import S3Service from '../../service/S3Service';
import VideoUploaderUtil from './VideoUploaderUtil.js'

export default {
  name: 'video-uploader',
  components: {
    draggable,
    VideoRow,
  },
  props: {},
  data() {
    return {
      dropIncoming: false,
      dropRefCount: 0,
      allowed_types: [ 'video/mp4', 'video/quicktime' ],
      util: null,
    };
  },
  computed: {
    acceptAttrib(){
      return this.allowed_types.join(', ');
    },
    videos: {
      get() {
        return this.$store.state.videos;
      },
      set(value) {
        this.$store.commit('setVideos', value);
        this.$store.dispatch('saveVideoReferencesToContentful');
      },
    },
    shouldShowDropzoneAndUploadButton() {
      // Hide only when it's in video mode, and it already has a video
      return !(this.util.isTargetedForVideo() && this.videos.length > 0);
    },
    dragOptions() {
      return {
        animation: 200,
        ghostClass: 'ghost',
        handle: '.handle',
      };
    },
    dropzoneMessage() {
      if (this.util.isTargetedForStory()) {
        return 'Drop files here to upload';
      } else if (this.util.isTargetedForVideo()) {
        return 'Drop a file here to upload';
      } else {
        return '';
      }
    },
  },
  created() {
    const ex = this.$extension;

    this.util = new VideoUploaderUtil(ex);

    Vue.prototype.$s3 = new S3Service(
      ex.parameters.installation.aws_s3_bucket_region,
      ex.parameters.installation.aws_s3_bucket_name,
      ex.parameters.installation.aws_access_key_id,
      ex.parameters.installation.aws_secret_access_key,
    );
  },
  mounted() {
    if (this.util.isTargetedForStory()) {
      console.log('targeted for story');
      this.$store.dispatch('loadVideosForThisStory');
    } else if (this.util.isTargetedForVideo()) {
      console.log('targeted for video');
      this.$store.dispatch('loadSingleVideoForThisEntry');
    }
  },
  methods: {
    handleDrop(ev) {
      let err = false;

      for (let i = 0; i < ev.dataTransfer.files.length; i++) {
        let file = ev.dataTransfer.files[i];

        if (this.allowed_types.indexOf(file.type) === -1) {
          err = true;
        } else {
          this.$store.commit('addVideo', file);

          if (this.util.isTargetedForVideo()) {
            // If it's in Video entry, allows only one video upload
            return;
          }
        }
      }

      if (err) {
        this.$extension.dialogs.openAlert({
          title: 'File type error',
          message: `One or more of the files you dropped in are unsupported formats.
            Please upload .mp4 or .mov files only.`
        });
      }
    },
    dropZoneEnter() {
      this.dropIncoming = true;

      // keep track of drag enter events, b/c firefox for instance fires
      // more drag* events on child element in the zone, event text content.
      // this allows us to know when we're back down to 0
      this.dropRefCount++;
    },
    dropZoneLeave() {
      this.dropRefCount--;

      if (this.dropRefCount === 0) {
        this.dropIncoming = false;
      }
    },
    startUploading() {
      console.log('start uploading');
      const fileInput = this.$refs.fileToUpload;
      const { files } = fileInput;
      console.log(files);
      if (files.length > 0) {
        const file = files[0];
        this.$store.commit('addVideo', file);
        fileInput.value = '';

        // In video mode, fill title if it's empty
        if (this.util.isTargetedForVideo()) {
          this.util.setTitleOfVideoEntryIfEmpty(file.name);
        }
      }
    },
    openVideoPicker() {
      const ex = this.$extension;
      const videoPickerId = ex.parameters.installation.video_picker_extension_id;
      ex.dialogs.openExtension({
        id: videoPickerId,
        width: 970,
        shouldCloseOnEscapePress: true,
        parameters: {
          idOfVideosInList: this.$store.getters.idOfVideos,
        },
      }).then(videos => {
        if (videos.length > 0) {
          this.$store.dispatch('addExistingVideosAndSaveVideoReferences', videos)
        }
      }).catch(err => {
        console.log('error happened in VideoPicker', err);
      });
    },
  },
};
</script>

<style scoped lang="scss">
  .sortable-list {
    ul {
      padding: 0;
    }
  }

  .button-row {
    display: flex;
    max-width: 540px;
    justify-content: center;

    div {
      margin: 0 10px;
    }
  }

  ul {
    margin-bottom: 0;
  }

  .upload-button {
    flex: initial;
    position: relative;

    [type=file] {
      position: absolute;
      filter: alpha(opacity=0);
      opacity: 0;
      top: 0;
      bottom: 0;
      width: 100%;
    }
    input,
    [type=file] + label {
      position: relative;
    }
    [type=file] + label {
      cursor: pointer;
    }
  }

  #videoPickerButton {
    user-select: none;
  }

  // Classes for Draggable
  .ghost {
    opacity: 0.25;
  }

  .note {
    text-align: center;
    padding: 10px 0;
    opacity: 0.5;
  }

  .drop-target {
    height: 65px;
    border: 1px dashed rgba(0, 0, 0, 0.25);
    display: flex;
    flex-direction: column;
    justify-content: center;
    align-items: center;
  }

  .drop-active {
    background-color: #e8f7ff;
    border: 1px solid #c5d2d8;
  }
</style>
